import { Braces, Check, Clipboard, Clock3, Database, LoaderCircle, Play, ShieldCheck, TerminalSquare, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { AgentChatResponse, SessionStatus } from '../shared/portalTypes';
import { ChatMarkdown } from './chatMarkdown';

export const GOVERNED_TOOL_NAMES = [
  'analyze_quote_pipeline_pulse',
  'analyze_renewal_command_center',
  'get_reseller_summary',
  'analyze_refresh_opportunity_book',
  'analyze_revenue_at_risk_workflow',
  'analyze_widget_workflow',
  'get_churn_risk'
] as const;
export type GovernedToolName = typeof GOVERNED_TOOL_NAMES[number];
type WidgetAnalysis = 'agedPipeline' | 'manufacturerCloseRate';

interface GovernedExecutionResponse {
  toolName: GovernedToolName;
  result: AgentChatResponse;
  durationMs: number;
  correlationId: string;
}

export function McpGovernedWorkbench({ toolName, sessionStatus, onConnect }: {
  toolName: GovernedToolName;
  sessionStatus: SessionStatus;
  onConnect(): void;
}): ReactNode {
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [analysis, setAnalysis] = useState<WidgetAnalysis>('agedPipeline');
  const [execution, setExecution] = useState<GovernedExecutionResponse>();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState<'report' | 'json'>('report');
  const [copied, setCopied] = useState(false);
  const hasDate = ['analyze_quote_pipeline_pulse', 'analyze_renewal_command_center', 'analyze_revenue_at_risk_workflow'].includes(toolName);

  const runTool = async () => {
    if (!sessionStatus.connected) { onConnect(); return; }
    setRunning(true); setError(''); setExecution(undefined);
    try {
      const argumentsValue = toolName === 'analyze_widget_workflow'
        ? { analysis }
        : hasDate ? { asOfDate } : {};
      const response = await fetch('/portal/mcp/governed/execute', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ toolName, arguments: argumentsValue })
      });
      const body = await response.json().catch(() => undefined) as GovernedExecutionResponse & { detail?: string; error?: string };
      if (!response.ok) throw new Error(body?.detail || body?.error || `MCP execution returned ${response.status}.`);
      setExecution(body);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The governed MCP tool could not be completed.');
    } finally { setRunning(false); }
  };

  const copyResult = async () => {
    if (!execution) return;
    await navigator.clipboard.writeText(JSON.stringify(execution.result, null, 2));
    setCopied(true); window.setTimeout(() => setCopied(false), 1400);
  };

  return <section className="mcp-run-workbench" aria-label={`Run ${toolName}`}>
    <header className="mcp-run-commandbar">
      <div className="mcp-run-context"><span><ShieldCheck aria-hidden="true" /> Governed read only</span><strong>{sessionStatus.environment.toUpperCase()}</strong><code>{sessionStatus.customerCode ?? 'No customer selected'}</code></div>
      <button className="mcp-run-button" type="button" disabled={running} onClick={() => void runTool()}>{running ? <LoaderCircle className="spinning" aria-hidden="true" /> : <Play aria-hidden="true" />}<span>{running ? 'Running' : sessionStatus.connected ? 'Run tool' : 'Connect to run'}</span></button>
    </header>
    <div className="mcp-run-body">
      <section className="mcp-run-inputs" aria-labelledby={`governed-inputs-${toolName}`}>
        <div className="mcp-run-section-title"><TerminalSquare aria-hidden="true" /><div><h4 id={`governed-inputs-${toolName}`}>Safe inputs</h4><p>Curated limits and tenant checks are enforced server-side.</p></div></div>
        {hasDate && <label className="mcp-run-field"><span>As-of date <em>UTC</em></span><input type="date" value={asOfDate} onChange={(event) => setAsOfDate(event.currentTarget.value)} /></label>}
        {toolName === 'analyze_widget_workflow' && <label className="mcp-run-field"><span>Analysis <em>Curated</em></span><select value={analysis} onChange={(event) => setAnalysis(event.currentTarget.value as WidgetAnalysis)}><option value="agedPipeline">Aged pipeline risk</option><option value="manufacturerCloseRate">Manufacturer close rate</option></select></label>}
        {!hasDate && toolName !== 'analyze_widget_workflow' && <div className="mcp-run-zero-input"><Check aria-hidden="true" /><span><strong>Governed preset</strong><small>{presetDescription(toolName)}</small></span></div>}
        <div className="mcp-run-invocation"><span>MCP invocation</span><code>{toolName}({toolName === 'analyze_widget_workflow' ? ` analysis: "${analysis}" ` : hasDate ? ` asOfDate: "${asOfDate}" ` : ''})</code></div>
      </section>
      <section className="mcp-run-results" aria-live="polite">
        <div className="mcp-run-resultbar"><div><Database aria-hidden="true" /><span><strong>Evidence report</strong><small>{execution ? `Completed in ${execution.durationMs.toLocaleString()} ms` : 'Deterministic MCP renderer'}</small></span></div>{execution && <div className="mcp-result-actions"><div className="mcp-result-view-toggle" aria-label="Result view"><button type="button" className={view === 'report' ? 'selected' : undefined} onClick={() => setView('report')}>Report</button><button type="button" className={view === 'json' ? 'selected' : undefined} onClick={() => setView('json')}>JSON</button></div><button type="button" title="Copy result JSON" onClick={() => void copyResult()}>{copied ? <Check aria-hidden="true" /> : <Clipboard aria-hidden="true" />}</button></div>}</div>
        {running && <div className="mcp-result-loading"><LoaderCircle className="spinning" aria-hidden="true" /><strong>Running {toolName}</strong><span>Opening a secure MCP session and validating tenant-scoped evidence.</span><div><i /><i /><i /></div></div>}
        {!running && error && <div className="mcp-run-error" role="alert"><TriangleAlert aria-hidden="true" /><span><strong>Execution failed</strong><p>{error}</p></span></div>}
        {!running && !error && !execution && <div className="mcp-result-empty"><Braces aria-hidden="true" /><strong>Ready for governed evidence</strong><span>Run the tool with your connected user's authorization.</span></div>}
        {!running && execution && view === 'json' && <pre className="mcp-result-json">{JSON.stringify(execution.result, null, 2)}</pre>}
        {!running && execution && view === 'report' && <div className="mcp-governed-report"><ChatMarkdown text={execution.result.reply} />{execution.result.toolRuns?.map((run) => <aside key={run.id} className="mcp-governed-evidence"><header><strong>{run.label}</strong><code>{run.name}</code></header>{run.facts && <div>{run.facts.map((fact) => <span key={`${fact.label}-${fact.value}`}><small>{fact.label}</small><strong>{fact.value}</strong></span>)}</div>}{run.warnings?.map((warning) => <p key={warning}>{warning}</p>)}</aside>)}</div>}
        {execution && <footer className="mcp-result-footer"><Clock3 aria-hidden="true" /><span>Correlation</span><code>{execution.correlationId}</code></footer>}
      </section>
    </div>
  </section>;
}

function presetDescription(toolName: GovernedToolName): string {
  if (toolName === 'get_reseller_summary') return 'Both quote and contract activity, Top 10 per safe currency partition.';
  if (toolName === 'analyze_refresh_opportunity_book') return 'Top 25 grouped opportunities per tenant and currency.';
  if (toolName === 'get_churn_risk') return 'Page 1, per-tenant Top 20, risk score 40+, no-renew evidence required.';
  return 'Server-owned bounded defaults.';
}
